import { computed, defineComponent, type FunctionalComponent, watch, onBeforeMount } from "vue";
import {
  EAssetType,
  getRefId,
  type FindexInvestment,
  notNullish,
} from "findef";
import { getInvestmentId } from "@/utils/investment";
import { GridRow } from "components/grid";
import Button from "components/button";
// @ts-ignore
import IconBriefcase from "components/icons/IconBriefcase.vue";
import PlusIcon from "assets/img/svg/icons/plus.svg?url";
import ForkIcon from "assets/img/svg/icons/fork.svg?url";
import { pxToRemStr } from "@/utils/style";
import { useDataTable } from "components/data-table/hooks/useDataTable";
import { DataTable } from "components/data-table";
import {
  useDataTableState,
  type UseDataTableState,
} from "components/data-table/hooks/useDataTableState";
import EmptyState from "components/empty-state";
import { Spinner } from "components/spinner";
import { useResponsive } from "~/composables/useResponsive";
import { EViewMode } from "~/composables/useView";
import { CircleBadge } from "components/circle-badge";
import IRClient from "@/modules/ir/apiClient";
import { useSelectMode } from "~/composables/useSelectMode/useSelectMode";
import { useInvestmentFilters } from "~/composables/useInvestmentFilters/useInvestmentFilters";
import { WithContextMenu } from "components/context-menu/components/with-context-menu";
import type { IContextMenuSection } from "components/context-menu/types";
import { createPaginatorView } from "@/utils/hooks/usePaginator";
import { ResponsiveAssetFilters } from "@/modules/asset/components/responsive-asset-filters";
import { useInvestmentRowsV2 } from "@/modules/asset/hooks/useInvestmentRowsV2";
import { type UseInvestments, useInvestments } from "@/modules/asset/hooks/useInvestments";
import { pushAddAssetModal } from "@/modules/asset/modals/add-asset";
import { showDeleteAssetPrompt } from "@/modules/asset/modals/delete-prompt";
import { pushEditAssetModal } from "@/modules/asset/modals/edit-asset";
import { pushImportAssetsModal } from "@/modules/asset/modals/import-assets";
import { pushPortfolioStructureModal } from "@/modules/asset/modals/portfolio-structure";
import EmptyTableState, { EEmptyTableStateType } from "components/empty-table-state";
import { pushToast } from "components/toast/hooks";
import { EAssetSource } from "findef";
import { FindexClient } from '@/integrations/findex'


definePageMeta({
  layout: 'app',
  breadcrumb: 'Assets',
  props: true
})

const AssetActions = defineComponent<{
  tableState: UseDataTableState;
  investment?: FindexInvestment;
  investmentHandle: UseInvestments;
  setSelectMode: (selectMode: boolean) => void;
  viewEditInvestment: (investment: FindexInvestment) => void;
}>(
  (props) => {
    const selectedIds = computed(() =>
      Array.from(new Set([
        ...props.tableState.selectedIds.value,
        ...(props.investment ? [getRefId(props.investment)] : [])
      ])),
    );

    const handleDelete = async () => {
      if (selectedIds.value.length > 0) {
        const investments = selectedIds.value
          .map(
            (id) =>
              props.investmentHandle.investments.value.find(
                (inv) => getRefId(inv) === id,
              ) || null,
          )
          .filter(notNullish);

        console.log('[DELETE LIST] Investments to delete:', investments);

        // Check if any asset directories have documents (with timeout to avoid blocking)
        const foldersWithDocuments: Array<{ assetId: string; assetName: string; keep: boolean }> = [];
        const assetIds = investments
          .filter(inv => inv.asset?.source !== EAssetSource.IR && inv.asset?._id)
          .map(inv => getRefId(inv.asset));

        console.log('[DELETE LIST] Asset IDs to check:', assetIds);

        if (assetIds.length > 0) {
          try {
            const timeoutPromise = new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Timeout')), 5000)
            );

            console.log('[DELETE LIST] Calling checkDirectoryHasDocuments with:', assetIds);
            const checkPromise = FindexClient.checkDirectoryHasDocuments(assetIds);
            const [results] = await Promise.race([checkPromise, timeoutPromise]) as [Array<{ assetId: string; hasDocuments: boolean }>, any];

            console.log('[DELETE LIST] Check results:', results);

            for (const result of results) {
              console.log('[DELETE LIST] Processing result:', result);
              if (result.hasDocuments) {
                const investment = investments.find(inv => getRefId(inv.asset) === result.assetId);
                if (investment) {
                  foldersWithDocuments.push({
                    assetId: result.assetId,
                    assetName: investment.asset.name || investment.name || result.assetId,
                    keep: true,
                  });
                  console.log('[DELETE LIST] Added folder:', foldersWithDocuments[foldersWithDocuments.length - 1]);
                }
              }
            }
          } catch (error) {
            // If check fails or times out, proceed without folder prompt
            console.warn('[DELETE LIST] Failed to check directory documents:', error);
          }
        } else {
          console.log('[DELETE LIST] No asset IDs to check (all IR or no IDs)');
        }

        console.log('[DELETE LIST] Final foldersWithDocuments:', foldersWithDocuments);

        const promptResult = await showDeleteAssetPrompt({
          investments,
          foldersWithDocuments,
        });

        const confirmed = typeof promptResult === 'boolean' ? promptResult : promptResult?.confirmed;
        if (!confirmed || investments.length === 0) return;

        // Extract keepFolders array if user made choices
        const keepFolders = typeof promptResult === 'object' && promptResult !== null && 'keepFolders' in promptResult ? promptResult.keepFolders : [];
        console.log('[DELETE LIST] Keep folders for assets:', keepFolders);

        try {
          // Delete all investments in a single bulk request to avoid the
          // global rate limiter (60 req/min) when many rows are selected.
          const idsToDelete = investments.map((inv) => getRefId(inv));
          const { deleted, failed } = await IRClient.deleteInvestments(idsToDelete);

          const assetsToDeleteFolders = investments
            .filter(inv => inv.asset?._id && !keepFolders.includes(getRefId(inv.asset)))
            .map(inv => getRefId(inv.asset));

          console.log('[DELETE LIST] Deleting folders for assets:', assetsToDeleteFolders);

          if (assetsToDeleteFolders.length > 0) {
            await IRClient.deleteAssetDirectories(assetsToDeleteFolders);
          }

          props.tableState.setAllSelected(false);
          props.setSelectMode(false);
          await props.investmentHandle.refresh();

          if (failed.length > 0 && deleted.length === 0) {
            const reasons = Array.from(new Set(failed.map(f => f.reason))).join(', ');
            pushToast({
              title: 'Deletion failed',
              subtitle: `Failed to delete ${failed.length} ${failed.length === 1 ? 'investment' : 'investments'} (${reasons}). No changes were made.`,
              variant: 'warning',
            });
          } else if (failed.length > 0) {
            const reasons = Array.from(new Set(failed.map(f => f.reason))).join(', ');
            pushToast({
              title: 'Partial deletion',
              subtitle: `Deleted ${deleted.length} ${deleted.length === 1 ? 'investment' : 'investments'}. ${failed.length} failed (${reasons}).`,
              variant: 'warning',
            });
          } else if (deleted.length > 0) {
            pushToast({
              title: 'Deleted',
              subtitle: `Successfully deleted ${deleted.length} ${deleted.length === 1 ? 'investment' : 'investments'}.`,
              variant: 'success',
            });
          }
        } catch (err) {
          console.error("Delete failed: ", err);
          pushToast({
            title: 'Error',
            subtitle: 'Failed to delete one or more investments. Please try again.',
            variant: 'warning'
          });
          return;
        }
      }
    };

    const menuSections = computed((): IContextMenuSection[] => {
      return [
        {
          items: [
            {
              testId: "row-action-edit",
              body: () => <div class="flex items-center">Edit</div>,
              disabled: computed(() => {
                return selectedIds.value.length > 1;
              }),
              iconLeft: {
                src: async () =>
                  await import("assets/img/svg/icons/edit-outline.svg?raw"),
                size: "1rem",
                fill: "currentColor",
              },
              action: () => {
                const selectedId = props.investment ? getRefId(props.investment) : selectedIds.value[0];
                if (!selectedId) return;
                const selectedInvestment =
                  props.investmentHandle.investments.value.find(
                    (it) => getRefId(it) === selectedId,
                  );
                if (!selectedInvestment) return;
                props.viewEditInvestment(selectedInvestment);
              },
            },
            {
              testId: "row-action-delete",
              body: () => (
                <div class="flex items-center text-red-600">Delete</div>
              ),
              iconLeft: {
                src: async () =>
                  await import("assets/img/svg/icons/trash-outline.svg?url"),
                size: "1rem",
                color: "#dc2626",
              },
              action: () => {
                handleDelete();
              },
            },
          ],
        },
      ];
    });

    return () => {
      return (
        <div
          class="relative "
          style={{
            width: "fit-content",
          }}
        >
          <WithContextMenu
            menu={{
              placement: "bottom-right",
              sections: menuSections,
              toggleWithClick: true,
            }}
          >
            <Button
              data-testid="row-actions-trigger"
              noFocusRing
              variant="white"
              transparent
              class="border border-gray-300 hover:border-primary-500 hover:bg-primary-100 hover:text-primary-500"
              onlyIcon
              icon={{
                src: async () =>
                  await import("assets/img/svg/icons/more_vertical.svg?url"),
                size: "1rem",
                fill: "currentColor",
              }}
            />
          </WithContextMenu>
        </div>
      );
    };
  },
  {
    props: [
      "tableState",
      "investment",
      "investmentHandle",
      "setSelectMode",
      "viewEditInvestment",
    ],
  },
);

const view = defineComponent({
  name: "PortfolioView",
  setup() {
    const route = useRoute();
    const router = useRouter();

    const tableFilters = useInvestmentFilters({
      disabledViewModes: [EViewMode.GRID],
    });
    const { isSelectMode, setSelectMode } = useSelectMode();
    const tableState = useDataTableState({});

    watch(isSelectMode, (newVal) => {
      if (!newVal) {
        tableState.setAllSelected(false);
      }
    }, { immediate: false })

    onBeforeMount(() => {
      setSelectMode(false)
    })

    const initFromUrl = () => {
      const type = route.query.assetType as EAssetType
      const listed = route.query.listed
      if (type) {
        tableFilters.selectAssetType(type)
        if (listed !== undefined) {
          tableFilters.selectListed(listed === 'true')
        }
      }
    }
    initFromUrl()
    const investmentHandle = useInvestments({
      list: {
        filters: {
          assetType: tableFilters.selectedAssetType,
          listed: computed(() =>
            tableFilters.selectedAssetType.value === EAssetType.UNDEFINED
              ? undefined
              : tableFilters.selectedListed.value,
          ),
          queryString: tableFilters.queryString,
        },
        sort: tableState.sorting,
      },
    });
    watch(
      () => route.query,
      (query) => {
        const type = query.assetType as EAssetType
        const listed = query.listed
        if (type) {
          tableFilters.selectAssetType(type)
          if (listed !== undefined) {
            tableFilters.selectListed(listed === 'true')
          }
        } else {
          // If no assetType in URL, reset to undefined
          tableFilters.selectAssetType(EAssetType.UNDEFINED)
          tableFilters.selectListed(false)
        }
      }, { immediate: true }
    );
    watch(
      [() => tableFilters.selectedAssetType.value, () => tableFilters.selectedListed.value],
      ([newType, newListed]) => {
        const newQuery: Record<string, string | undefined> = Object.fromEntries(
          Object.entries(route.query).map(([key, value]) => [key, Array.isArray(value) ? value[0] ?? undefined : value ?? undefined])
        )
        if (newType === EAssetType.UNDEFINED) {
          delete newQuery.assetType
          delete newQuery.listed
        } else {
          newQuery.assetType = newType as string
          if (newListed !== undefined) {
            newQuery.listed = String(newListed)
          } else {
            delete newQuery.listed
          }
        }
        router.replace({
          query: newQuery
        }).catch((err) => console.error(err))
      }
    )

    const investments = investmentHandle.investments;
    const totalInvestedFormatted = investmentHandle.totalInvestedFormatted;
    const totalCount = investmentHandle.totalCount;
    const totalPaginationCount = investmentHandle.filteredCount;
    const paginator = investmentHandle.paginator;

    watch(
      [
        () => tableFilters.selectedAssetType.value,
        () => tableFilters.selectedListed.value,
        () => tableFilters.queryString.value,
      ],
      () => {
        paginator.setPage(0);
      }
    );



    const refreshInvestments = async (resetFilters: boolean = true) => {
      if (resetFilters) {
        tableFilters.reset();
      }
      await investmentHandle.refresh();
    };

    const showPortfolioStructureModal = () => {
      pushPortfolioStructureModal(investmentHandle.forex);
    };

    const viewInvestment = (investment: FindexInvestment) => {
      const id = getInvestmentId(investment);
      router.push(`/portfolio/investments/${id}`);
    };

    const viewAddAsset = () => {
      pushAddAssetModal({
        onSave: () => refreshInvestments(true),
      });
    };

    const handleAddAsset = () => {
      viewAddAsset();
    };

    if (route.query.openAddAssetModal === "true") {
      handleAddAsset();
    }

    const viewEditInvestment = (investment: FindexInvestment) => {
      const id = getInvestmentId(investment);
      pushEditAssetModal({
        investmentId: id,
        investment: JSON.parse(JSON.stringify(investment)),
        onSave: () => refreshInvestments(false),
        onDeleted: () => refreshInvestments(true),
      });
    };
    const handleClickMaintainted = (investment: FindexInvestment) => {
      const providerImport = investment?.providerImport as any;
      if (!providerImport) return;
      const onComplete = () => {
        investmentHandle.refresh();
      };
      pushImportAssetsModal({
        providerImport: providerImport,
        onSave: onComplete,
        hideBackButtonIndex: 1,
        breadcrumbs: false,
      });
    };

    const handleClickInvestment = (investment: FindexInvestment) => {
      viewInvestment(investment);
    };

    const responsive = useResponsive();

    const { rows } = useInvestmentRowsV2(investments, {
      onMaintainedClick: handleClickMaintainted,
      onInvestmentClick: handleClickInvestment,
      onInvestmentsUpdated: () => refreshInvestments(false),
      columns: (inv) => {
        return computed(() => {
          return [
            {
              align: "right",
              direction: "ltr",
              label: "Actions",
              width: 1,
              body: () => {
                return (
                  <GridRow
                    style={{ direction: "ltr" }}
                    layout="max-content max-content"
                    gap="0.5rem"
                  >
                    <div
                      class={[
                        isSelectMode.value
                          ? "opacity-40 pointer-events-none p-2 text-xs font-medium border border-gray-300 justify-center items-center inline-flex text-gray-500 "
                          : "p-2 text-xs font-medium border border-gray-300 justify-center items-center inline-flex cursor-pointer text-gray-500 hover:border-primary-500 hover:bg-primary-100 hover:text-primary-500",
                      ]}
                      onClick={() => {
                        if (!isSelectMode.value) viewInvestment(inv);
                      }}
                    >
                      Open
                    </div>
                    {isSelectMode.value ? (
                      <Button
                        data-cy="edit-investment"
                        variant="transparent"
                        onlyIcon
                        icon={{
                          src: async () =>
                            await import(
                              "assets/img/svg/icons/more_vertical.svg?url"
                            ),
                          size: "12px",
                          fill: "currentColor",
                        }}
                        outlined
                        disabled={isSelectMode.value}
                      />
                    ) : (
                      <AssetActions
                        tableState={tableState}
                        investment={inv}
                        investmentHandle={investmentHandle}
                        setSelectMode={setSelectMode}
                        viewEditInvestment={viewEditInvestment}
                      />
                    )}
                  </GridRow>
                );
              },
            },
          ];
        });
      },
    });

    const emptyStateType = computed(() => {
      if (totalCount.value <= 0) return EEmptyTableStateType.NO_ASSETS;
      if (
        tableFilters.queryString.value &&
        tableFilters.queryString.value.length > 0
      )
        return EEmptyTableStateType.QUERY_NOT_FOUND;
      if (tableFilters.selectedAssetType.value !== EAssetType.UNDEFINED)
        return EEmptyTableStateType.FILTER_NOT_FOUND;
      return undefined;
    });

    const pagi = createPaginatorView({
      paginator: paginator,
      totalCount: totalPaginationCount,
    });

    const isLoading = computed(() => investmentHandle.loading.value);
    const initLoading = computed(() => investmentHandle.init.value);

    const table = useDataTable({
      loading: isLoading,
      rows: rows,
      options: {
        rows: {
          selectable: isSelectMode,
        },
        selection: {
          action: () =>
            isSelectMode.value ?
              <div
                class="relative "
                style={{
                  width: "fit-content",
                }}
              >{tableState.selectionCount.value > 0 && (<CircleBadge
                variant="blue"
                style={{
                  top: "-7px",
                  width: "1rem",
                  height: "1rem",
                  fontSize: "10px",
                }}
                value={tableState.selectionCount.value}
              />)}

                <AssetActions
                  tableState={tableState}
                  investmentHandle={investmentHandle}
                  setSelectMode={setSelectMode}
                  viewEditInvestment={viewEditInvestment}
                />
              </div> : "ACTIONS"
        },
      },
      paginator: pagi,
      state: tableState,
      schema: {
        columns: [
          { key: "asset.name" },
          { key: "asset.source" },
          { key: "value3" },
          { key: "ROI.value" },
          { key: "time" },
        ],
      },
    });

    const EmptyStateAction: FunctionalComponent = () => {
      return (
        <Button
          icon={{
            src: PlusIcon,
            stroke: "white",
            fill: "currentColor",
            size: "0.8rem",
          }}
          onClick={handleAddAsset}
        >
          Add Asset
        </Button>
      );
    };

    return () => {
      if (initLoading.value) {
        return (
          <div class="w-full h-full flex items-center justify-center">
            <Spinner />
          </div>
        );
      }
      return (
        <div class="w-full h-full">
          {(totalCount.value > 0 || isLoading.value) && (
            <DataTable
              shadow
              rounded
              loading={isLoading}
              emptyState={() => (
                <EmptyTableState
                  type={emptyStateType.value}
                  assetType={tableFilters.selectedAssetType.value}
                  listed={tableFilters.selectedListed.value}
                />
              )}
              banner={{
                header: {
                  title: "Asset List",
                  subtitle: `${totalCount.value} assets in total valued at ${totalInvestedFormatted.value}`,
                },
                fullWidthActions: computed(() => responsive.isSmall.value),
                actions: [
                  {
                    body: () => {
                      const buttonHeight = pxToRemStr(40);
                      return responsive.isSmall.value ? (
                        <div class="w-full grid grid-cols-[1fr_1fr] gap-4 items-center justify-between">
                          <Button
                            height={buttonHeight}
                            onClick={showPortfolioStructureModal}
                            variant="secondary"
                            textClass="font-semibold"
                            fitText
                            icon={{
                              src: ForkIcon,
                              size: 14,
                              fill: "currentColor",
                            }}
                          >
                            Ownership
                          </Button>
                          <Button
                            height={buttonHeight}
                            onClick={viewAddAsset}
                            class="whitespace-nowrap"
                          >
                            + Asset
                          </Button>
                        </div>
                      ) : (
                        <GridRow layout="max-content max-content" gap="1rem">
                          <Button
                            height={buttonHeight}
                            onClick={showPortfolioStructureModal}
                            variant="secondary"
                            textClass="font-semibold"
                            fitText
                            icon={{
                              src: ForkIcon,
                              size: 14,
                              fill: "currentColor",
                            }}
                          >
                            Ownership Structure
                          </Button>
                          <Button
                            height={buttonHeight}
                            onClick={viewAddAsset}
                            class="whitespace-nowrap"
                          >
                            + Add Asset
                          </Button>
                        </GridRow>
                      );
                    },
                  },
                ],
                body: computed(() => {
                  return (
                    <div>
                      <ResponsiveAssetFilters filters={tableFilters} disabled={true} />
                    </div>
                  );
                }),
              }}
              table={table}
              variant="white"
              borderlessHeader
            />
          )}
          {totalCount.value <= 0 && !isLoading.value && (
            <div class="w-full h-full flex items-center justify-center">
              <EmptyState
                title="No Assets In Portfolio"
                subtitle="Add your first asset to start setting up your investment portfolio!"
                icon={() => <IconBriefcase />}
                action={EmptyStateAction}
              />
            </div>
          )}
        </div>
      );
    };
  },
});

export default view;
